/**
 * Smart Cart drawer — see sections/smart-cart.liquid.
 * Cart mutations go through the Cart AJAX API and re-render the drawer with the
 * Section Rendering API. Emits the theme's `cart:update` event so the header
 * bubble and product forms stay in sync.
 */
(() => {
  // Not `window.SmartCart`: the drawer's id="SmartCart" makes that name resolve to the element.
  if (window.__smartCartLoaded) return;
  window.__smartCartLoaded = true;

  const SOURCE = 'smart-cart';
  const getRoot = () => document.querySelector('[data-smart-cart]');
  const sectionId = () => getRoot()?.dataset.sectionId;

  let queue = Promise.resolve();
  let lastFocus = null;
  let giftFailed = false;
  let externalTimer = null;

  const readState = () => {
    const el = getRoot()?.querySelector('[data-sc-part="state"]');
    try {
      return el ? JSON.parse(el.textContent) : null;
    } catch (_) {
      return null;
    }
  };

  const isCartPage = () => (window.Theme?.template?.name || '').startsWith('cart');

  /* ---------- rendering ---------- */

  function swap(html) {
    const root = getRoot();
    if (!root || !html) return;
    // A stepper change is still waiting to be sent: keep its on-screen numbers. That request re-renders after.
    if (stepTimers.size) return;
    const next = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-smart-cart]');
    if (!next) return;

    root.dataset.empty = next.dataset.empty;

    next.querySelectorAll('[data-sc-part]').forEach((incoming) => {
      const current = root.querySelector(`[data-sc-part="${incoming.dataset.scPart}"]`);
      if (!current) return;

      // Keep the progress fill animating from its old width to the new one.
      const oldFill = current.querySelector('.sc__fill');
      const newFill = incoming.querySelector('.sc__fill');
      const target = newFill?.style.width;
      if (oldFill && newFill) newFill.style.width = oldFill.style.width;

      current.replaceWith(incoming);

      if (oldFill && newFill) {
        void newFill.offsetWidth;
        newFill.style.width = target;
      }
    });
  }

  async function renderSection() {
    const id = sectionId();
    if (!id) return;
    const res = await fetch(`${window.location.pathname}?sections=${encodeURIComponent(id)}`, {
      credentials: 'same-origin',
    });
    const json = await res.json();
    swap(json[id]);
  }

  function announce() {
    const state = readState();
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: { resource: {}, sourceId: SOURCE, data: { source: SOURCE, itemCount: state?.itemCount ?? 0 } },
      })
    );
  }

  function showError(message) {
    const root = getRoot();
    if (!root) return;
    let el = root.querySelector('.sc__error');
    if (!el) {
      el = document.createElement('p');
      el.className = 'sc__error';
      el.setAttribute('role', 'alert');
      el.style.cssText =
        'margin:0;padding:10px 22px;font-size:12.5px;line-height:1.4;color:#B4463A;background:#FBEDEB;text-align:center;';
      root.querySelector('.sc__body')?.prepend(el);
    }
    el.textContent = message;
    clearTimeout(el._t);
    el._t = setTimeout(() => el.remove(), 6000);
  }

  /* ---------- cart API ---------- */

  async function post(url, body, withSections = true) {
    const payload = { ...body };
    if (withSections && sectionId()) {
      payload.sections = sectionId();
      payload.sections_url = window.location.pathname;
    }
    const res = await fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Smart-Cart': '1' },
      body: JSON.stringify(payload),
    });
    const json = await res.json();
    if (!res.ok || json.status) {
      const err = new Error(json.description || json.message || 'Something went wrong. Please try again.');
      err.response = json;
      throw err;
    }
    if (withSections && json.sections) swap(json.sections[sectionId()]);
    return json;
  }

  const update = () =>
    window.Shopify?.routes?.root ? `${window.Shopify.routes.root}cart/update.js` : '/cart/update.js';

  const routes = () => ({
    add: window.Shopify?.routes?.root ? `${window.Shopify.routes.root}cart/add.js` : '/cart/add.js',
    change: window.Shopify?.routes?.root ? `${window.Shopify.routes.root}cart/change.js` : '/cart/change.js',
  });

  function enqueue(task) {
    const root = getRoot();
    root?.classList.add('is-busy');
    queue = queue
      .then(task)
      .then(() => syncGift())
      .catch((err) => {
        console.error('[smart-cart]', err);
        showError(err.message);
        return renderSection().catch(() => {});
      })
      .finally(() => {
        getRoot()?.classList.remove('is-busy');
        announce();
      });
    return queue;
  }

  // 14 Oct 2026: the drawer's line keys go stale when the cart changes elsewhere (another tab, the
  // theme preview, back from checkout). Shopify then answers "no valid id or line parameter" and
  // customers saw that raw text on Remove. Re-read the cart and retry once on the same variant.
  const isStaleKey = (err) => /valid id or line/i.test(err?.message || '');

  const changeLine = (key, quantity) =>
    enqueue(async () => {
      try {
        await post(routes().change, { id: key, quantity });
      } catch (err) {
        if (!isStaleKey(err)) throw err;
        await renderSection();
        const variant = String(key).split(':')[0];
        const lines = [...(getRoot()?.querySelectorAll('.sc__item:not(.sc__item--gift)[data-key]') || [])].filter(
          (li) => li.dataset.key.split(':')[0] === variant
        );
        if (lines.length !== 1) {
          throw new Error('Your cart was updated in another window. Please check it and try again.');
        }
        await post(routes().change, { id: lines[0].dataset.key, quantity });
      }
    });

  const changeVariant = (key, variantId, quantity) =>
    enqueue(async () => {
      await post(routes().change, { id: key, quantity: 0 }, false);
      await post(routes().add, { items: [{ id: Number(variantId), quantity }] });
    });

  /* ---------- free gift ---------- */

  async function syncGift() {
    const state = readState();
    if (!state) return;

    const giftId = String(state.giftVariantId || '').trim();
    // Kit is SpiroPlus-only: kitSubtotal counts SpiroPlus lines alone (see sections/smart-cart.liquid).
    const kitSubtotal = state.kitSubtotal ?? state.subtotal;
    const wants = Boolean(giftId) && (state.kitCount ?? state.paidCount) > 0 && kitSubtotal >= state.giftThreshold;

    if (wants && !state.giftKey && !giftFailed) {
      try {
        await post(routes().add, { items: [{ id: Number(giftId), quantity: 1, properties: { _sc_gift: '1' } }] });
      } catch (err) {
        giftFailed = true; // don't retry on every change if the gift variant can't be added
        console.warn('[smart-cart] gift not added:', err.message);
      }
    } else if (!wants && state.giftKey) {
      await post(routes().change, { id: state.giftKey, quantity: 0 });
    } else if (wants && state.giftQty > 1) {
      // Keep one kit line at qty 1. A cart-recovery link once left two kits in one order (#1628).
      const keys = (state.giftKeys || []).filter(Boolean);
      if (keys.length > 1) {
        const updates = {};
        keys.forEach((key, i) => (updates[key] = i === 0 ? 1 : 0));
        await post(update(), { updates });
      } else {
        await post(routes().change, { id: state.giftKey, quantity: 1 });
      }
    }
  }

  /* ---------- open / close ---------- */

  function open() {
    const root = getRoot();
    if (!root) {
      window.location.href = '/cart';
      return;
    }
    if (root.classList.contains('is-open')) return;
    // Re-read the cart on every open so the line keys match the real cart (see changeLine).
    // Skipped while an add is in flight: that add re-renders the drawer itself.
    if (!root.classList.contains('is-loading')) refresh();
    lastFocus = document.activeElement;
    root.hidden = false;
    document.documentElement.classList.add('sc-lock');
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        root.classList.add('is-open');
        root.querySelector('.sc__drawer')?.focus({ preventScroll: true });
      })
    );
  }

  function close() {
    const root = getRoot();
    if (!root || !root.classList.contains('is-open')) return;
    root.classList.remove('is-open');
    document.documentElement.classList.remove('sc-lock');
    setTimeout(() => {
      if (!root.classList.contains('is-open')) root.hidden = true;
    }, 360);
    if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus({ preventScroll: true });
  }

  const refresh = () => {
    queue = queue.then(renderSection).then(syncGift).catch((err) => console.error('[smart-cart]', err));
    return queue;
  };

  /* ---------- events ---------- */

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const opener = target.closest('[data-smart-cart-open]');
    if (opener && getRoot()) {
      event.preventDefault();
      open();
      return;
    }

    const root = getRoot();
    if (!root || !root.contains(target)) return;

    if (target.closest('[data-sc-close]')) {
      event.preventDefault();
      close();
      return;
    }

    // Empty cart button pointing at the page we're already on: close and go to the plans
    // instead of reloading to the top (seen on the SpiroPlus PDP).
    const emptyLink = target.closest('.sc__empty a[href]');
    if (emptyLink) {
      const url = new URL(emptyLink.href, window.location.href);
      const plans = document.getElementById('spiro-bundle-selector');
      if (url.pathname === window.location.pathname && plans) {
        event.preventDefault();
        close();
        setTimeout(() => plans.scrollIntoView({ behavior: 'smooth', block: 'start' }), 250);
        return;
      }
    }

    const qtyBtn = target.closest('[data-sc-qty]');
    if (qtyBtn) {
      event.preventDefault();
      // Remove asks once: people were removing the paid bottle by mistake and emptying the cart.
      if (qtyBtn.hasAttribute('data-sc-confirm') && !qtyBtn.classList.contains('is-confirm')) {
        const label = qtyBtn.textContent;
        qtyBtn.classList.add('is-confirm');
        qtyBtn.textContent = 'Tap again to remove (free kit goes too)';
        clearTimeout(qtyBtn._t);
        qtyBtn._t = setTimeout(() => {
          qtyBtn.classList.remove('is-confirm');
          qtyBtn.textContent = label;
        }, 4000);
        return;
      }
      const stepper = qtyBtn.closest('.sc__qty');
      if (stepper) {
        step(stepper, qtyBtn, qtyBtn.dataset.key);
        return;
      }
      clearTimeout(stepTimers.get(qtyBtn.dataset.key)); // Remove wins over a pending +/−
      stepTimers.delete(qtyBtn.dataset.key);
      syncStepping();
      changeLine(qtyBtn.dataset.key, Math.max(0, parseInt(qtyBtn.dataset.scQty, 10) || 0));
    }
  });

  /* ---------- quantity stepper ----------
   * 14 Oct 2026: each tap used to send its own request and grey out the list until Shopify answered
   * (~1 s per tap, more on mobile data), so 1 → 3 felt stuck. Now the number, line price and totals
   * change on the tap, quick taps are collected for 400 ms, and one request saves the final quantity.
   * Checkout stays disabled (.is-stepping / .is-busy) until the server total is back.
   */
  const stepTimers = new Map();
  const syncStepping = () => getRoot()?.classList.toggle('is-stepping', stepTimers.size > 0);

  const MONEY_RE = /\$\s?[\d,]+(?:\.\d{2})?/;
  const money = (cents) =>
    '$' + (cents / 100).toLocaleString('en-SG', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const shiftMoney = (el, delta) => {
    if (!el) return;
    const m = el.textContent.match(MONEY_RE);
    if (!m) return;
    el.textContent = el.textContent.replace(MONEY_RE, money(toCents(m[0]) + delta));
  };

  function step(stepper, button, key) {
    const span = stepper.querySelector('span');
    const [minus, plus] = stepper.querySelectorAll('button');
    const current = parseInt(span?.textContent, 10) || 1;
    const next = Math.max(1, current + (button === plus ? 1 : -1));
    if (next === current) return;

    span.textContent = String(next);
    minus.disabled = next <= 1;
    minus.dataset.scQty = String(next - 1);
    plus.dataset.scQty = String(next + 1);

    const delta = (next - current) * (parseInt(stepper.dataset.unit, 10) || 0);
    const root = getRoot();
    shiftMoney(stepper.closest('.sc__item')?.querySelector('[data-sc-line-price]'), delta);
    shiftMoney(root?.querySelector('.sc__total strong'), delta);
    shiftMoney(root?.querySelector('.sc__checkout'), delta);

    clearTimeout(stepTimers.get(key));
    stepTimers.set(
      key,
      setTimeout(() => {
        stepTimers.delete(key);
        syncStepping();
        changeLine(key, next);
      }, 400)
    );
    syncStepping();
  }

  document.addEventListener('change', (event) => {
    const select = event.target instanceof HTMLSelectElement ? event.target : null;
    if (!select || !select.matches('[data-sc-variant]')) return;
    changeVariant(select.dataset.key, select.value, parseInt(select.dataset.qty, 10) || 1);
  });

  document.addEventListener('keydown', (event) => {
    const root = getRoot();
    if (!root || !root.classList.contains('is-open')) return;

    if (event.key === 'Escape') {
      close();
      return;
    }

    if (event.key === 'Tab') {
      const focusable = [
        ...root.querySelectorAll('a[href], button:not([disabled]), select, input, [tabindex]:not([tabindex="-1"])'),
      ].filter((el) => el.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && (document.activeElement === first || !root.contains(document.activeElement))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  });

  // Something outside the drawer changed the cart (product form, quick add, custom sections).
  function onExternalAdd() {
    // Open straight away; the contents refresh in behind the loading bar.
    getRoot()?.classList.add('is-loading'); // before open(), so open() doesn't queue a second render
    if (getRoot()?.dataset.autoOpen === 'true' && !isCartPage()) open();
    clearTimeout(externalTimer);
    externalTimer = setTimeout(async () => {
      await refresh();
      getRoot()?.classList.remove('is-loading');
      announce();
    }, 50);
  }

  document.addEventListener('cart:update', (event) => {
    const data = event.detail?.data || {};
    if (data.source === SOURCE || event.detail?.sourceId === SOURCE) return;
    if (data.didError) {
      refresh();
      return;
    }
    if (data.source === 'product-form-component') {
      onExternalAdd();
    } else {
      refresh();
    }
  });

  // Catch AJAX adds from scripts that don't emit the theme event (page builders, custom sections).
  const isAddUrl = (url) => /\/cart\/add(\.js)?(\?|$)/.test(String(url || ''));

  const nativeFetch = window.fetch.bind(window);
  window.fetch = function (input, init) {
    const url = typeof input === 'string' ? input : input?.url;
    const headers = init?.headers;
    const ours =
      headers && (headers instanceof Headers ? headers.get('X-Smart-Cart') : headers['X-Smart-Cart']);
    const promise = nativeFetch(input, init);
    if (!ours && isAddUrl(url)) {
      promise.then((res) => res.ok && onExternalAdd()).catch(() => {});
    }
    return promise;
  };

  const nativeOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url, ...rest) {
    if (isAddUrl(url)) {
      this.addEventListener('load', () => {
        if (this.status >= 200 && this.status < 300) onExternalAdd();
      });
    }
    return nativeOpen.call(this, method, url, ...rest);
  };

  // Restore a correct drawer when the page comes back from the back/forward cache.
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) refresh();
  });
  // ...and when the customer comes back to this tab (the cart may have changed in another one).
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && getRoot()) refresh();
  });

  // Theme editor: show the drawer while its section is selected.
  document.addEventListener('shopify:section:select', (event) => {
    if (event.detail?.sectionId && event.detail.sectionId === sectionId()) open();
  });
  document.addEventListener('shopify:section:deselect', (event) => {
    if (event.detail?.sectionId && event.detail.sectionId === sectionId()) close();
  });
  document.addEventListener('shopify:section:load', (event) => {
    if (event.detail?.sectionId && event.detail.sectionId === sectionId()) open();
  });

  /* ---------- SpiroPlus bundle selector (templates/product.json) ----------
   * Its form posts natively to /cart/add with return_to=/checkout, and the sticky bar
   * calls form.submit() to land on /cart. Both skip the drawer, so take them over here:
   * add over AJAX, then open the drawer. Without the drawer, the native flow still runs.
   */
  const BUNDLE_FORM = '#spxBundleForm, #v2form';

  // "SGD 259" / "$259.00" -> 25900 cents.
  const toCents = (text) => {
    const n = parseFloat(String(text || '').replace(/[^0-9.]/g, ''));
    return Number.isFinite(n) ? Math.round(n * 100) : 0;
  };

  // Shown the instant the customer clicks, replaced by the real line when the add returns.
  function showPendingLine(name, price) {
    const root = getRoot();
    const items = root?.querySelector('[data-sc-part="items"]');
    if (!items) return;
    const row = document.createElement('li');
    row.className = 'sc__item sc__item--pending';
    row.innerHTML =
      '<div class="sc__item-media"></div><div class="sc__item-info"><div class="sc__item-top">' +
      '<p class="sc__item-title"></p><p class="sc__item-price"></p></div>' +
      '<p class="sc__item-tagline">Adding to your cart…</p></div>';
    row.querySelector('.sc__item-title').textContent = name;
    row.querySelector('.sc__item-price').textContent = price;
    let list = items.querySelector('.sc__items');
    if (!list) {
      items.innerHTML = '<ul class="sc__items" role="list"></ul>';
      list = items.firstChild;
    }
    list.prepend(row);
    root.dataset.empty = 'false';
  }

  async function addBundle(form) {
    if (getRoot()?.classList.contains('is-loading')) return; // ignore double taps mid-add
    const active = document.querySelector('[data-spx-wrap] .spx-card.is-active');
    const tile = document.querySelector('#plans .tile.sel'); // PDP v2 plan tile
    // data-price / data-price-sale (11.11) on the card; the old .spx-price text ("$259/ $2.88/day") parsed to the wrong cents.
    const saleOn = document.documentElement.classList.contains('is-1111') && active?.hasAttribute('data-price-sale');
    const priceText = active?.getAttribute(saleOn ? 'data-price-sale' : 'data-price') || active?.querySelector('.spx-price')?.textContent.trim() || tile?.dataset.pay || '';
    const addedCents = toCents(priceText);

    // Open first: the customer sees the drawer and their item immediately, not after the network.
    getRoot()?.classList.add('is-loading'); // before open(), so open() doesn't queue a second render
    open();
    showPendingLine(active?.querySelector('.spx-name')?.textContent.trim() || tile?.querySelector('.nm')?.textContent.trim() || 'SpiroPlus', priceText);

    const body = new FormData(form);
    body.delete('return_to');

    // Add the free gift in the same request when this add crosses the threshold (saves a round trip).
    const state = readState();
    const giftId = String(state?.giftVariantId || '').trim();
    const isSpiroForm = !form.hasAttribute('data-no-kit'); // the Tiger Milk King picker sets data-no-kit
    if (isSpiroForm && giftId && !state.giftKey && addedCents && (state.kitSubtotal ?? state.subtotal) + addedCents >= state.giftThreshold) {
      body.append('items[9][id]', giftId);
      body.append('items[9][quantity]', '1');
      body.append('items[9][properties][_sc_gift]', '1');
    }

    // Ask for the re-rendered drawer in the same response instead of a second request.
    if (sectionId()) {
      body.append('sections', sectionId());
      body.append('sections_url', window.location.pathname);
    }

    queue = queue
      .then(async () => {
        const res = await fetch(routes().add, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { Accept: 'application/json', 'X-Smart-Cart': '1' },
          body,
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok || json.status) {
          await renderSection();
          showError(json.description || json.message || 'Could not add to cart.');
          return;
        }
        if (json.sections) swap(json.sections[sectionId()]);
        else await renderSection();
        await syncGift(); // no request unless the gift needs correcting
      })
      .catch(async (err) => {
        console.error('[smart-cart] bundle add failed', err);
        await renderSection().catch(() => {});
        showError('Could not add to cart. Please try again.');
      })
      .finally(() => {
        getRoot()?.classList.remove('is-loading');
        announce();
      });
    return queue;
  }

  // Loading styles for the instant-open state. Kept here so the section file doesn't change.
  const loadingStyle = document.createElement('style');
  loadingStyle.textContent = `
    .sc__drawer::before { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 3px; z-index: 2;
      background: linear-gradient(90deg, transparent, var(--sc-fresh), var(--sc-accent), transparent);
      background-size: 50% 100%; background-repeat: no-repeat; opacity: 0; transition: opacity 150ms ease; }
    .sc.is-loading .sc__drawer::before { opacity: 1; animation: sc-loading 1s linear infinite; }
    @keyframes sc-loading { from { background-position: -50% 0; } to { background-position: 150% 0; } }
    .sc.is-loading [data-sc-part="totals"], .sc.is-loading [data-sc-part="checkout"] { opacity: 0.5; }
    .sc.is-loading [data-sc-part="checkout"] { pointer-events: none; }
    .sc__item--pending .sc__item-media { animation: sc-pulse 1.1s ease-in-out infinite; }
    @keyframes sc-pulse { 50% { opacity: 0.45; } }
    @media (prefers-reduced-motion: reduce) {
      .sc.is-loading .sc__drawer::before, .sc__item--pending .sc__item-media { animation: none; }
    }`;
  document.head.appendChild(loadingStyle);

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.matches(BUNDLE_FORM) || !getRoot()) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      addBundle(form);
    },
    true
  );

  // Capture phase on document runs before the sticky button's own click handler.
  document.addEventListener(
    'click',
    (event) => {
      const button = event.target instanceof Element ? event.target.closest('#spxScrollBtn') : null;
      const form = document.querySelector(BUNDLE_FORM);
      if (!button || !form || !getRoot()) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      const active = document.querySelector('[data-spx-wrap] .spx-card.is-active');
      if (active && active.getAttribute('data-available') !== 'true') return;
      addBundle(form);
    },
    true
  );

  // Reconcile the free gift once on load (e.g. cart changed on another tab or on /cart).
  const init = () => {
    if (!getRoot()) return;
    queue = queue.then(syncGift).catch((err) => console.warn('[smart-cart]', err));
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  window.SmartCart = { open, close, refresh };
})();
